// components/document/common/document-upload-modal.component.jsx
import {
  Button,
  Collapse,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { useState } from 'react';

import { useStyledToast } from '@util/hooks';
import { createFileValidator, maxFileSizeBytes } from '@util/helpers';
import { MAX_DOC_FILE_SIZE_MB, ACCEPTED_DOC_FILE_FORMATS } from '@util/constants';

import DocumentDropzone from '@components/layout/summarize/document-dropzone.component';

/**
 * Generic document upload modal that handles file uploads with validation and submission
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.disclosure - Chakra disclosure object containing isOpen and onClose
 * @param {React.ReactNode} props.headerIcon - Icon to display in the modal header
 * @param {string} props.headerText - Text to display in the modal header
 * @param {string} props.submitLabel - Label for the submit button
 * @param {string} [props.submitTestId] - Test ID for the submit button
 * @param {Function} props.onSubmit - Async function that handles the submission with file data
 * @param {Function} [props.onError] - Optional error handler function
 * @param {Function} [props.infoToastTitle] - Optional function that returns toast title text
 * @returns {React.ReactElement} Document upload modal component
 */
const DocumentUploadModal = ({
  disclosure,
  headerIcon,
  headerText,
  submitLabel,
  submitTestId,
  onSubmit,
  onError,
  infoToastTitle
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useStyledToast();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
    setValue,
    clearErrors,
    reset
  } = useForm();

  const validateFile = createFileValidator({ maxSizeMB: MAX_DOC_FILE_SIZE_MB });

  const onFormSubmit = async ({ file, fileName }) => {
    setIsSubmitting(true);

    if (infoToastTitle && file) {
      toast({
        status: 'info',
        title: infoToastTitle(file),
        containerProps: { minW: '0px', w: 'fit-content', maxW: 'unset' },
        titleProps: { maxW: '475px' },
        duration: 10000,
        isClosable: true
      });
    }

    try {
      await onSubmit({ file, fileName });
      reset();
      disclosure.onClose();
    } catch (err) {
      onError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      {...disclosure}
      variant='classic'
      scrollBehavior='inside'
      onCloseComplete={() => {
        reset();
        setIsSubmitting(false);
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            {headerIcon}
            <Text variant='body-600-md'>{headerText}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          data-testid='document-upload-modal'
          p={6}
        >
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <Stack gap={6}>
              <Stack gap={2}>
                <Text
                  variant='body-600-sm'
                  color={errors?.file ? 'critical.primary' : 'surface.onSurface'}
                >
                  Upload File
                </Text>
                <Controller
                  control={control}
                  name='file'
                  rules={{ required: 'Add a file' }}
                  render={({ field: { value, onChange } }) => (
                    <DocumentDropzone
                      file={value}
                      onFileLoad={(file) => {
                        onChange(file);
                        setValue('fileName', file.name);
                      }}
                      onFileRemove={() => setValue('fileName', '')}
                      validateFile={validateFile}
                      maxFileSize={maxFileSizeBytes(MAX_DOC_FILE_SIZE_MB)}
                      helperText={{
                        heading: 'Drag & drop document files here',
                        subtext: `Upload PDF, CSV, TXT, DOC(X), XLS(X) (Max size: ${MAX_DOC_FILE_SIZE_MB}MB)`
                      }}
                      accept={ACCEPTED_DOC_FILE_FORMATS}
                      onError={(error) => {
                        if (!error) {
                          clearErrors('file');
                        } else {
                          setError('file', { message: error.message });
                          setValue('fileName', '');
                        }
                      }}
                    />
                  )}
                />
                <Collapse in={!!errors?.file}>
                  <Text
                    color='critical.primary'
                    variant='label-600-md'
                  >
                    {errors?.file?.message}
                  </Text>
                </Collapse>
              </Stack>

              <Stack gap={2}>
                <Text
                  variant='body-600-sm'
                  color={errors?.fileName ? 'critical.primary' : 'surface.onSurface'}
                >
                  File Name
                </Text>
                <Input
                  placeholder='Your file name'
                  {...register('fileName', { required: 'Add a file name' })}
                />
                <Collapse in={!!errors?.fileName}>
                  <Text
                    color='critical.primary'
                    variant='label-600-md'
                  >
                    {errors?.fileName?.message}
                  </Text>
                </Collapse>
              </Stack>

              {/* Footer: errors banner + submit */}
              <HStack w='full'>
                <Collapse
                  style={{ width: '100%' }}
                  in={!!errors?.fileName || !!errors?.file}
                >
                  <HStack
                    h={10}
                    w='full'
                    bgColor='critical.containerLow'
                    borderRadius='8px'
                  >
                    <Text
                      px={5}
                      py={2}
                      color='critical.onContainer'
                      variant='body-reg-sm'
                    >
                      Please resolve the required fields
                    </Text>
                  </HStack>
                </Collapse>
                <Spacer />
                <Button
                  alignSelf='end'
                  type='submit'
                  variant='reskinPrimary'
                  size='reskinLg'
                  borderRadius='lg'
                  isLoading={isSubmitting}
                  data-testid={submitTestId}
                >
                  {submitLabel}
                </Button>
              </HStack>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DocumentUploadModal;
